import React, { useState, useEffect } from 'react';
import { FiSun, FiThermometer, FiDroplet, FiCloud, FiRefreshCw, FiSettings } from 'react-icons/fi';
import api from '../../../../../services/api';
import WeatherChart from './WeatherChart';
import './WeatherTab.css';

const WeatherCard = ({ icon: Icon, label, value, unit, status, timestamp }) => (
  <div className={`weather-card ${status ? `weather-${status}` : ''}`}>
    <Icon className="weather-icon" />
    <div className="weather-info">
      <span className="weather-value">{value ?? 'N/A'}{unit}</span>
      <span className="weather-label">{label}</span>
      {timestamp && (
        <span className="weather-timestamp">
          {new Date(timestamp * 1000).toLocaleTimeString()}
        </span>
      )}
    </div>
  </div>
);

const WeatherControls = ({ serial, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [location, setLocation] = useState('');
  const [showConfig, setShowConfig] = useState(false);

  const handleWeatherAction = async (action, value = null) => {
    if (api.loading.isWeatherControlling(serial)) return;
    
    setLoading(true);
    try {
      const params = value ? { value } : {};
      await api.device.weatherControl(serial, action, params);
      // REMOVED: onRefresh() - Don't check device status after weather commands
      // Device status checks should only be done for checkout/payment flows
      console.log(`Weather ${action} completed successfully`);
    } catch (error) {
      console.error(`Weather ${action} failed:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    if (apiKey) {
      await handleWeatherAction('APIKEY', apiKey);
      setApiKey('');
    }
    if (location) {
      await handleWeatherAction('LOCATION', location);
      setLocation('');
    }
    setShowConfig(false);
  };

  return (
    <div className="weather-controls">
      <div className="weather-actions">
        <button 
          className="weather-btn refresh-btn"
          onClick={() => handleWeatherAction('FETCH')}
          disabled={loading}
        >
          <FiRefreshCw className={loading ? 'spinning' : ''} />
          Refresh Weather
        </button>
        
        <button 
          className="weather-btn config-btn"
          onClick={() => setShowConfig(!showConfig)}
        >
          <FiSettings />
          Configure
        </button>
      </div>

      {showConfig && (
        <form className="weather-config" onSubmit={handleConfigSubmit}>
          <div className="config-group">
            <label>OpenWeather API Key:</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter API key"
            />
          </div>
          <div className="config-group">
            <label>Location:</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="City, State or Lat,Lon"
            />
          </div>
          <div className="config-actions">
            <button type="submit" disabled={!apiKey && !location}>
              Save Configuration
            </button>
            <button type="button" onClick={() => setShowConfig(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const WeatherTab = ({ serial }) => {
  const [blockStatus, setBlockStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchWeatherData = async () => {
    if (!serial) return;
    
    setLoading(true);
    try {
      // Get current block status which includes weather data from heartbeat
      const response = await api.block.getBlockStatus(serial);
      const status = response?.data || response;
      
      setBlockStatus(status);
      setLastUpdate(new Date().toISOString());
    } catch (error) {
      console.error('Failed to fetch weather data:', error);
      setBlockStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeatherData();
  }, [serial]);

  if (loading && !blockStatus) return <p>Loading weather data…</p>;
  
  const weatherData = blockStatus?.weather_data || {};
  const weatherValid = blockStatus?.weather_valid;
  const weatherTimestamp = blockStatus?.weather_timestamp;
  const uvIndex = blockStatus?.uv_index;
  const temperature = blockStatus?.temperature_f;
  const humidity = blockStatus?.humidity;
  const weatherConditions = blockStatus?.weather_conditions;

  const hasWeatherData = weatherValid && (weatherTimestamp || uvIndex !== undefined);

  return (
    <div className="weather-tab">
      <div className="weather-header">
        <h3>Weather & Environmental Data</h3>
        {lastUpdate && (
          <span className="last-update">
            Last updated: {new Date(lastUpdate).toLocaleTimeString()}
          </span>
        )}
      </div>

      <WeatherControls serial={serial} onRefresh={fetchWeatherData} />

      {hasWeatherData ? (
        <div className="weather-data">
          <div className="weather-cards">
            <WeatherCard 
              icon={FiSun} 
              label="UV Index" 
              value={uvIndex !== undefined ? uvIndex : 'N/A'}
              status={weatherValid ? 'valid' : 'stale'}
              timestamp={weatherTimestamp}
            />
            <WeatherCard 
              icon={FiThermometer} 
              label="Temperature" 
              value={temperature !== undefined && temperature !== 'nan' ? temperature : 'N/A'} 
              unit="°F"
              status={weatherValid ? 'valid' : 'stale'}
              timestamp={weatherTimestamp}
            />
            <WeatherCard 
              icon={FiDroplet} 
              label="Humidity" 
              value={humidity !== undefined && humidity !== 'nan' ? humidity : 'N/A'} 
              unit="%"
              status={weatherValid ? 'valid' : 'stale'}
              timestamp={weatherTimestamp}
            />
          </div>
          
          {weatherConditions && (
            <div className="weather-conditions">
              <FiCloud className="conditions-icon" />
              <span className="conditions-text">{weatherConditions}</span>
            </div>
          )}
          
          <div className="weather-status">
            <span className={`status-indicator ${weatherValid ? 'valid' : 'stale'}`}>
              {weatherValid ? 'Live Weather Data' : 'Stale Weather Data'}
            </span>
          </div>
        </div>
      ) : (
        <div className="no-weather-data">
          <p>No weather data available.</p>
          <p>Configure weather settings and refresh to get current conditions.</p>
        </div>
      )}
      
      {/* Daily Weather Analytics Charts */}
      <div className="weather-analytics-section">
        <h3>📊 Weather History & Trends</h3>
        <p className="analytics-description">
          Daily weather patterns and UV index trends based on dispense events with weather data
        </p>
        <WeatherChart serial={serial} />
      </div>
    </div>
  );
};

export default WeatherTab;